import { AuthStatusOptionModel } from "../../../data-model/authStatusOptions";
import { BadgeWithIconProps } from "../../../data-model/badgeWithIconProps";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../data-model/medicalPaOrdersModel";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../../enums/evBvWriteBackStatus";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import {
  financialStatusOptions,
  MedicalPaOrderStatus,
} from "../../../enums/medicalPaOrderStatus";
import {
  isExceptionFlowForRadiologyOrg,
  isExceptionFlowForUrologyOrg,
} from "../../../enums/organizationTypes";
import { determineIfDocumentRpaShouldRun } from "../../../utils/determineIfDocumentRpaShouldRun";
import { getAuthStatusOfMedicalPaOrderFromAuthOnFile } from "../../../utils/getAuthStatusOfMedicalPaOrder";

/**
 * Checks if the submission page should be shown based on the order's auth status.
 *
 * @param generalOrderDetails - Postgres-backed general order details
 * @param authOnFileDetails - Auth-on-file record for auth status resolution
 * @param authStatusOptions - Available auth status options for mapping
 * @returns true if submission page should be shown, false otherwise
 */
export const checkIfSubmissionPageShouldBeShown = (
  generalOrderDetails: GeneralOrderDetails | null | undefined,
  authOnFileDetails: AuthOnFile | null | undefined,
  authStatusOptions: AuthStatusOptionModel[] | undefined,
): boolean => {
  if (!generalOrderDetails || !authStatusOptions) {
    return false;
  }

  const getAuthStatus = getAuthStatusOfMedicalPaOrderFromAuthOnFile(
    authOnFileDetails as AuthOnFile,
    authStatusOptions,
  ).id;

  if (getAuthStatus === MedicalPaOrdersAuthStatus.AuthOnFile) {
    return false;
  }

  if (getAuthStatus === MedicalPaOrdersAuthStatus.AuthRequired) {
    // Prefer the unified Postgres `submission_status`; fall back to the legacy
    // `submission` field (using `||` so an empty PG value also falls back)
    // for orders not yet served from the PG unified column.
    const submissionStatus =
      generalOrderDetails?.status?.submission_status
    // Treat an empty/absent status as "no submission" (e.g. after a resubmit
    // clears it) so the submission page is not shown.
    if (
      !submissionStatus ||
      submissionStatus === MedicalPaOrderStatus.PAYER_NOT_SUPPORTED
    ) {
      return false;
    }
    return true;
  }

  return false;
};

/**
 * Gets the current status badge for the order based on financial review status.
 *
 * @param generalOrderDetails - Postgres-backed general order details
 * @returns The badge configuration for the current status, or undefined if not found
 */
export const getCurrentStatusBadge = (
  generalOrderDetails: GeneralOrderDetails | null | undefined,
): BadgeWithIconProps | undefined => {
  if (!generalOrderDetails) {
    return undefined;
  }

  const currentStatus =
    generalOrderDetails?.status?.financial_review ?? MedicalPaOrderStatus.NEW;
  const currentStatusBadge = financialStatusOptions.find(
    (option) => option.id === currentStatus,
  );
  return currentStatusBadge;
};

/**
 * Gets the tab switch elements for the single-order layout, optionally
 * filtered by organization type, facility category, and facility feature flags.
 *
 * Per-org overrides:
 * - **Radiology exception-flow** (`category === "radiology"`): drops the
 *   "Visit Data Chart" tab and relabels "Drugs" → "Scan" and
 *   "Business Office" → "Instructions".
 * - **Urology exception-flow orgs** (`exceptionFlowForUrologyOrgs`): drops the
 *   "Visit Data Chart" and "Business Office" tabs, relabels "Drugs" →
 *   "Procedures", and may append an "Auth Notes" tab.
 *
 * @param orgId - Organization ID used for urology exception-flow overrides
 * @param includeTasks - When true, includes the OncoEMR Tasks tab
 * @param enableAuthLetterUploadToCandid - When true, includes the Candid Update tab
 * @param category - Healthcare facility `category` used for radiology overrides
 * @returns Array of tab elements with `id` and `label`
 */
export const getTabSwitchElements = (
  orgId: string | null | undefined,
  includeTasks = false,
  enableAuthLetterUploadToCandid = false,
  category?: string | null,
): Array<{ id: string; label: string }> => {
  const allElements = [
    { id: "cptCodes", label: "Drugs" },
    { id: "comments", label: "Business Office" },
    { id: "visitData", label: "Visit Data Chart" },
    { id: "documents", label: "Documents" },
    { id: "authLetters", label: "Auth Letters" },
    ...(includeTasks ? [{ id: "tasks", label: "Tasks" }] : []),
    { id: "queryComments", label: "Query Comments" },
    ...(enableAuthLetterUploadToCandid
      ? [{ id: "candidUpdate", label: "Candid Update" }]
      : []),
  ];

  if (isExceptionFlowForUrologyOrg(orgId)) {
    const sunstateLabelOverrides: Record<string, string> = {
      cptCodes: "Procedures",
    };
    return [
      ...allElements
        .filter(
          (element) => element.id !== "visitData" && element.id !== "comments",
        )
        .map((element) => ({
          ...element,
          label: sunstateLabelOverrides[element.id] ?? element.label,
        })),
    ];
  }

  if (!isExceptionFlowForRadiologyOrg(category)) {
    return allElements;
  }

  const elementsWithoutVisitData = allElements.filter(
    (element) => element.id !== "visitData",
  );
  const radiologyLabelOverrides: Record<string, string> = {
    cptCodes: "Scan",
    comments: "Instructions",
  };
  return elementsWithoutVisitData.map((element) => ({
    ...element,
    label: radiologyLabelOverrides[element.id] ?? element.label,
  }));
};

/**
 * Determines if the happy modal should be opened based on write-back statuses.
 *
 * @param generalOrderDetails - Postgres-backed general order details
 * @param orderId - The order ID
 * @param authOnFile - Auth-on-file payload; drives the document-RPA gating
 *   decision used to choose between EV-only and EV+Doc completion paths.
 * @returns true if happy modal should be opened, false otherwise
 */
export const shouldOpenHappyModal = (
  generalOrderDetails: GeneralOrderDetails | null | undefined,
  orderId: string | null | undefined,
  authOnFile: AuthOnFile | null | undefined,
): boolean => {
  if (!generalOrderDetails || !orderId) {
    return false;
  }

  const evWriteBackStatusForHappyModal = [
    EVWriteBackStatus.SUCCESS,
    EVWriteBackStatus.IN_PROGRESS,
    EVWriteBackStatus.QUEUED,
  ];
  const documentUploadStatusForHappyModal = [
    DocWriteBackStatus.SUCCESS,
    DocWriteBackStatus.IN_PROGRESS,
    DocWriteBackStatus.QUEUED,
  ];
  const evStatus = generalOrderDetails?.status?.ev_write_back_status;
  const docStatus = generalOrderDetails?.status?.document_upload_status;

  if (!evStatus) return false;

  if (determineIfDocumentRpaShouldRun(authOnFile)) {
    if (!docStatus) return false;
    return (
      evWriteBackStatusForHappyModal.includes(evStatus as EVWriteBackStatus) &&
      documentUploadStatusForHappyModal.includes(
        docStatus as DocWriteBackStatus,
      )
    );
  }

  return evWriteBackStatusForHappyModal.includes(evStatus as EVWriteBackStatus);
};
